import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { screenPath, toPriceText } from '@commerce/core'
import { YALO_PALETTE, YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { LabelledRow, Screen } from '../design'
import { useDemo } from '../demo'
import { usePresentationContext } from '../hooks/usePresentationContext'
// The receipt rows and the payment screen's summary rows are the same rows.
// The shared home for this is `src/design`, which this agent does not own.

/**
 * The receipt.
 *
 * Everything on it comes out of demo state, because the order does: there is no
 * place-order mutation in the ported domain (`docs/NOT_PORTED.md`), so
 * `useDemo().placeOrder` is what recorded it. The one real thing that happened
 * during checkout was the cart being emptied.
 *
 * Reached without a recent order — a reload drops demo state — it says so and
 * offers the order history rather than rendering a receipt with blanks in it.
 */
export const ConfirmedPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const { distributor, orders, lastOrderId } = useDemo()
  const presentation = usePresentationContext()

  const order = orders.find((entry) => entry.id === lastOrderId)

  if (!order) {
    return (
      <Screen testId="confirmed-page">
        <Stack spacing={2} sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 5, textAlign: 'center' }}>
          <Typography variant="h5" component="h1">
            No encontramos un pedido reciente
          </Typography>
          <Typography variant="body2" sx={{ color: YALO_SEMANTIC.fg3 }}>
            Si acabas de hacer un pedido, lo verás en tu historial.
          </Typography>
          <Button component={RouterLink} to={screenPath('orders', { slug, sessionId })} variant="contained" size="large" fullWidth>
            Ver mis pedidos
          </Button>
          <Button component={RouterLink} to={screenPath('catalog', { slug, sessionId })} fullWidth>
            Volver al inicio
          </Button>
        </Stack>
      </Screen>
    )
  }

  const totalText = toPriceText(order.total, presentation)

  return (
    <Screen testId="confirmed-page">
      <Stack spacing={2.25} sx={{ flex: 1, p: 3, pt: 5 }}>
        <Box
          aria-hidden
          sx={{
            width: 72,
            height: 72,
            borderRadius: YALO_RADIUS.pill,
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 34,
            // The design's 20%-opacity green disc behind the checkmark.
            bgcolor: 'rgba(64, 228, 169, 0.2)',
            color: '#0B6B4C',
          }}
        >
          ✓
        </Box>

        <Stack spacing={0.75} sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h1">
            ¡Pedido confirmado!
          </Typography>
          <Typography variant="body2" sx={{ color: YALO_SEMANTIC.fg2 }}>
            Tu pedido fue recibido por {distributor.name}.
          </Typography>
        </Stack>

        <Stack
          spacing={1.5}
          sx={{
            bgcolor: YALO_SEMANTIC.bgSurface,
            border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
            borderRadius: `${YALO_RADIUS.lg}px`,
            p: 2,
          }}
        >
          {/* The id is a Mongo ObjectId — correct, and not an order number a
              person can read back to anyone. */}
          <LabelledRow label="Número de pedido" value={order.reference ?? order.id} />
          <LabelledRow label="Entrega" value={order.delivery} />
          <LabelledRow label="Pago" value={order.payment} />
          <Divider />
          {/* `toPriceText` returns an empty string when the storefront hides
              prices, and a receipt whose total is blank looks like a bug. */}
          <LabelledRow
            label="Total"
            value={totalText === '' ? 'No disponible' : totalText}
            strong
          />
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Button
          component={RouterLink}
          to={screenPath('orderDetail', { slug, sessionId, orderId: order.id })}
          variant="contained"
          size="large"
          fullWidth
        >
          Ver mi pedido
        </Button>
        <Button
          component={RouterLink}
          to={screenPath('catalog', { slug, sessionId })}
          size="large"
          fullWidth
          sx={{ bgcolor: YALO_PALETTE.mist }}
        >
          Volver al inicio
        </Button>
      </Stack>
    </Screen>
  )
}
